// File: frontend/src/components/common/CookieConsentBanner.tsx

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaCookieBite } from "react-icons/fa";
import { useCookieConsent, CookieConsent, DENIED_CONSENT } from "../../context/CookieConsentContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { getLegalVersions, LEGAL_ROUTES } from "../../services/legalService";
import "./CookieConsentBanner.css";

// ── Reusable preference panel ─────────────────────────────────────────────────

interface CookiePreferencePanelProps {
    /** Called with the chosen categories when the user saves. */
    onSave: (consent: CookieConsent) => void;
    /** Optional cancel action — omitted on the standalone preferences page. */
    onCancel?: () => void;
    /** Starting state of the toggles. */
    initial: CookieConsent;
}

/**
 * The three consent categories with their toggles.
 *
 * Shared by the banner's "manage" view and the standalone /cerez-tercihleri
 * page, so the two can never drift apart in what they offer.
 */
export const CookiePreferencePanel: React.FC<CookiePreferencePanelProps> = ({
    onSave,
    onCancel,
    initial,
}) => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(initial.analytics);
    const [ads, setAds] = useState(initial.ads);

    return (
        <div className="cookie-prefs">
            {/* Necessary — always on, rendered disabled so the user can see it exists */}
            <div className="cookie-pref-row">
                <div className="cookie-pref-text">
                    <span className="cookie-pref-title">
                        {t("cookie_pref_necessary_title", "Zorunlu kayıtlar")}
                    </span>
                    <span className="cookie-pref-desc">
                        {t(
                            "cookie_pref_necessary_desc",
                            "Oturum, dil, tema ve tercihleriniz. Bunlar olmadan platform çalışmaz, kapatılamaz."
                        )}
                    </span>
                </div>
                <label className="cookie-switch cookie-switch--locked">
                    <input type="checkbox" checked readOnly disabled />
                    <span className="cookie-switch-slider" />
                </label>
            </div>

            {/* Analytics */}
            <div className="cookie-pref-row">
                <div className="cookie-pref-text">
                    <span className="cookie-pref-title">
                        {t("cookie_pref_analytics_title", "Ölçümleme")}
                    </span>
                    <span className="cookie-pref-desc">
                        {t(
                            "cookie_pref_analytics_desc",
                            "Hangi sayfaların ne kadar görüntülendiğini toplu istatistik olarak ölçer."
                        )}
                    </span>
                </div>
                <label className="cookie-switch">
                    <input
                        type="checkbox"
                        checked={analytics}
                        onChange={(e) => setAnalytics(e.target.checked)}
                    />
                    <span className="cookie-switch-slider" />
                </label>
            </div>

            {/* Advertising */}
            <div className="cookie-pref-row">
                <div className="cookie-pref-text">
                    <span className="cookie-pref-title">
                        {t("cookie_pref_ads_title", "Reklam")}
                    </span>
                    <span className="cookie-pref-desc">
                        {t(
                            "cookie_pref_ads_desc",
                            "Gördüğünüz reklamların kişiselleştirilmesi. Kapalıyken reklamlar kişiselleştirilmez."
                        )}
                    </span>
                </div>
                <label className="cookie-switch">
                    <input
                        type="checkbox"
                        checked={ads}
                        onChange={(e) => setAds(e.target.checked)}
                    />
                    <span className="cookie-switch-slider" />
                </label>
            </div>

            <div className="cookie-prefs-actions">
                {onCancel && (
                    <button type="button" className="cookie-btn cookie-btn--ghost" onClick={onCancel}>
                        {t("cancel", "Vazgeç")}
                    </button>
                )}
                <button
                    type="button"
                    className="cookie-btn cookie-btn--primary"
                    onClick={() => onSave({ necessary: true, analytics, ads })}
                >
                    {t("cookie_save_preferences", "Tercihleri kaydet")}
                </button>
            </div>
        </div>
    );
};

// ── Banner ────────────────────────────────────────────────────────────────────

/**
 * Bottom consent bar shown until the visitor makes a decision.
 *
 * Reject is given the same visual weight as accept: the Cookie Policy states
 * that declining is a real option, and a bar that hides the decline button
 * would make that statement untrue.
 */
export const CookieConsentBanner: React.FC = () => {
    const { t, i18n } = useTranslation();
    const {
        consent,
        needsDecision,
        preferencesOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
        setPolicyVersion,
    } = useCookieConsent();
    const { cookieBannerEnabled } = useSiteSettings();

    // Learn the current Cookie Policy version so a choice made under an older
    // version can be invalidated — this is what makes "esaslı değişiklikte
    // tercihiniz sıfırlanır" in the policy text actually happen.
    useEffect(() => {
        let cancelled = false;
        getLegalVersions(i18n.language)
            .then((versions) => {
                if (!cancelled && versions.COOKIE) setPolicyVersion(versions.COOKIE);
            })
            .catch(() => {
                // Version unknown — the stored choice is honoured as-is rather
                // than nagging the visitor because of a failed request
            });
        return () => {
            cancelled = true;
        };
    }, [i18n.language, setPolicyVersion]);

    // Admin can switch the bar off entirely; in that case nothing optional runs
    // either, because the consent state stays at its denied default.
    if (!cookieBannerEnabled) return null;

    // The preferences dialog can be opened from the footer even after a decision
    if (!needsDecision && !preferencesOpen) return null;

    // Rendered through a portal on document.body rather than inline in the tree.
    // A position:fixed element is positioned against the viewport only while none
    // of its ancestors creates a containing block; any ancestor carrying transform,
    // filter or will-change silently makes the bar position against that ancestor
    // instead, which is how it ended up floating in the middle of the page on
    // mobile. Attaching to body removes that dependency entirely.
    return createPortal(
        <div className="cookie-banner" role="dialog" aria-live="polite">
            <div className="cookie-banner-inner">
                {preferencesOpen ? (
                    <>
                        <div className="cookie-banner-head">
                            <span className="cookie-banner-icon"><FaCookieBite /></span>
                            <span className="cookie-banner-title">
                                {t("cookie_prefs_title", "Çerez Tercihleri")}
                            </span>
                        </div>
                        <CookiePreferencePanel
                            initial={consent}
                            onSave={savePreferences}
                            onCancel={needsDecision ? undefined : closePreferences}
                        />
                    </>
                ) : (
                    <>
                        <div className="cookie-banner-message">
                            <span className="cookie-banner-icon"><FaCookieBite /></span>
                            <p className="cookie-banner-text">
                                {t(
                                    "cookie_banner_text",
                                    "Platformun çalışması için gereken kayıtları tarayıcınızda tutuyoruz. Ölçümleme ve reklam çerezleri yalnızca siz onay verirseniz devreye girer."
                                )}{" "}
                                <Link to={LEGAL_ROUTES.COOKIE} className="cookie-banner-link">
                                    {t("legal_link_cookie", "Çerez Politikası")}
                                </Link>
                            </p>
                        </div>

                        <div className="cookie-banner-actions">
                            <button
                                type="button"
                                className="cookie-btn cookie-btn--ghost"
                                onClick={openPreferences}
                            >
                                {t("cookie_manage", "Tercihleri yönet")}
                            </button>
                            <button
                                type="button"
                                className="cookie-btn cookie-btn--secondary"
                                onClick={rejectAll}
                            >
                                {t("cookie_reject_all", "Reddet")}
                            </button>
                            <button
                                type="button"
                                className="cookie-btn cookie-btn--primary"
                                onClick={acceptAll}
                            >
                                {t("cookie_accept_all", "Kabul et")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

/** Exported so the standalone preferences page can start from a denied baseline. */
export { DENIED_CONSENT };